import { polygon } from "@turf/helpers";
import centroid from "@turf/centroid";

import MapConstants from "@/constants/mapConstants";

import MapHelpers from "@/helpers/MapHelpers";
import PersonaHelpers from "@/helpers/PersonaHelpers";
import { updateMapContentRoute } from "@/helpers/RouteHelpers";

import StyleJsonService from "@/services/StyleJsonService";

export default {
  namespaced: true,
  state: () => ({
    map: undefined,
    mapDraw: undefined,
    mapMode: "",
    zoom: 13,
    minZoom: 1,
    maxZoom: 23.5,
    currentClient: undefined,
    currentSite: undefined,
    currentBuilding: undefined,
    currentLevel: undefined,
    isMapReady: false,
    isAIMappingInProgress: false,
    guidanceMessage: undefined,
    shouldShowMapIcons: {
      search: {
        shouldShow: true,
        isDisabled: false
      },
      zoom: {
        shouldShow: true,
        isDisabled: false
      },
      cursor: {
        shouldShow: false,
        isDisabled: false
      },
      help: {
        shouldShow: false,
        isDisabled: false
      },
      buildingLevelSelector: {
        shouldShow: false,
        isDisabled: false
      },
      pathLevelSelector: {
        shouldShow: false,
        isDisabled: false
      }
    },
    drawnCoordinates: undefined,
    clickedTransitionFeature: undefined,
    pointMarkers: [],
    shouldDisplayOriginalFloorPlan: false,
    shouldDisplayLevelManager: false,
    shouldDisplayFloorAlignment: false,
    isBoundarySet: false,
    shouldPreventBuildingUpdateOnMapMove: false,
    isBuildingChangeInProgress: false,
    expandedTypeCodeBeforeEdit: null,
    highlightedFeatureIds: [],
    isEsriMap: false,
    esriCopyrightText: undefined,
    isStyleJsonLoaded: false,
    shouldDisplayOriginalFloorPlanToggle: false
  }),
  mutations: {
    MAP(state, map) {
      state.map = map;
    },
    MAP_DRAW(state, mapDraw) {
      state.mapDraw = mapDraw;
    },
    MAP_MODE(state, mode) {
      state.mapMode = mode;
    },
    ZOOM(state, zoom) {
      if (zoom === state.zoom) {
        return;
      } else if (zoom < state.minZoom || zoom - state.minZoom < 0.1) {
        state.zoom = state.minZoom;
      } else if (zoom > state.maxZoom || state.maxZoom - zoom < 0.1) {
        state.zoom = state.maxZoom;
      }
      state.zoom = zoom;
      if (state.map && state.isMapReady) {
        state.map.jumpTo({
          zoom: zoom
        });
      }
    },
    HIDE_UI_ICONS(state, iconName) {
      if (iconName) {
        Object.keys(state.shouldShowMapIcons).some((i) => {
          if (i === iconName) {
            state.shouldShowMapIcons[i].shouldShow = false;
            return true;
          }
        });
      } else {
        Object.keys(state.shouldShowMapIcons).forEach((i) => (state.shouldShowMapIcons[i].shouldShow = false));
      }
    },
    SHOW_UI_ICON(state, { iconName }) {
      if (!(iconName in state.shouldShowMapIcons)) {
        console.warn(`UI icon ${iconName} wouldn't be visible - Invalid UI icon name`, state.shouldShowMapIcons);
        return;
      }
      state.shouldShowMapIcons[iconName].shouldShow = true;
    },
    SHOULD_DISPLAY_ORIGINAL_FLOOR_PLAN(state, value) {
      state.shouldDisplayOriginalFloorPlan = value;
    },
    TOGGLE_UI_ICON_DISABILITY_STATE(state, { iconName, isDisabled }) {
      state.shouldShowMapIcons[iconName].isDisabled = isDisabled;
    },
    CURRENT_CLIENT(state, client) {
      state.currentClient = client;
    },
    CURRENT_SITE(state, site) {
      state.currentSite = site;
    },
    CURRENT_BUILDING(state, building) {
      state.currentBuilding = building;
    },
    BUILDING_CHANGE_IN_PROGRESS(state, value) {
      state.isBuildingChangeInProgress = value;
    },
    CURRENT_LEVEL(state, level) {
      if (level !== undefined && level !== "NaN" && level !== "null" && level !== "undefined") {
        state.currentLevel = Number(level);
      } else {
        state.currentLevel = undefined;
      }
    },
    MAP_READY(state, value) {
      state.isMapReady = value;
    },
    DRAWN_COORDINATES(state, value) {
      state.drawnCoordinates = value;
    },
    IS_AI_MAPPING_IN_PROGRESS(state, value) {
      state.isAIMappingInProgress = value;
    },
    GUIDANCE_MESSAGE(state, value) {
      state.guidanceMessage = value;
    },
    CLICKED_TRANSITION_FEATURE(state, value) {
      state.clickedTransitionFeature = { ...value };
    },
    POINT_MARKERS(state, value) {
      state.pointMarkers = value;
    },
    SHOULD_DISPLAY_LEVEL_MANAGER(state, value) {
      state.shouldDisplayLevelManager = value;
    },
    SHOULD_DISPLAY_FLOOR_ALIGNMENT(state, value) {
      state.shouldDisplayFloorAlignment = value;
    },
    IS_BOUNDARY_SET(state, value) {
      state.isBoundarySet = value;
    },
    SET_MAP_LAYOUT_PROPERTY(state, { layer, property, value }) {
      if (state.map) {
        state.map.setLayoutProperty(layer, property, value);
      }
    },
    SHOULD_PREVENT_BUILDING_UPDATE_ON_MAP_MOVE(state, value) {
      state.shouldPreventBuildingUpdateOnMapMove = value;
    },
    SET_EXPANDED_TYPECODE_BEFORE_EDIT(state, { buildingId, levelId, typeCode }) {
      state.expandedTypeCodeBeforeEdit = { buildingId, levelId, typeCode };
    },
    SET_HIGHLIGHTED_FEATURE_IDS(state, value) {
      if (value.length > 0) {
        state.highlightedFeatureIds = [...new Set([...state.highlightedFeatureIds, ...value])];
      } else {
        state.highlightedFeatureIds = [];
      }
    },
    SET_ESRI_COPYRIGHT_TEXT(state, value) {
      state.esriCopyrightText = value;
    },
    SET_IS_ESRI_MAP(state, value) {
      state.isEsriMap = value;
    },
    SET_IS_STYLE_JSON_LOADED(state, value) {
      state.isStyleJsonLoaded = value;
    },
    SHOULD_DISPLAY_ORIGINAL_FLOOR_PLAN_TOGGLE(state, value) {
      state.shouldDisplayOriginalFloorPlanToggle = value;
    }
  },
  actions: {
    // TODO: move to mutation (except sync and ones do 2 different commits)
    SET_CURRENT_CLIENT({ commit, rootState }, payload) {
      const clientObject = rootState.CONTENT.clients.find((client) => client.identifier === payload.client);
      if (clientObject) {
        commit("CURRENT_CLIENT", payload.client);
      }
    },
    SET_CURRENT_SITE({ state, commit, getters }, payload) {
      const site = payload.site;
      if (state.currentSite == site && !payload.forceUpdate) {
        MapHelpers.fitBoundsIfNotOnTheScreen(getters?.currentSiteObject?.geometry?.coordinates?.[0]);
        return;
      }
      commit("CURRENT_SITE", site);
      const siteObj = getters.currentSiteObject;
      if (siteObj?.geometry?.coordinates?.[0] === undefined) {
        return;
      }
      // MapHelpers.fitBounds(siteObj.geometry.coordinates[0]);

      // Update route parameters
      updateMapContentRoute(state);
    },
    SET_CURRENT_BUILDING({ state, commit }, payload) {
      const building = payload.building;
      if (state.currentBuilding == building && !payload.forceUpdate) {
        return;
      }

      // Set flag to prevent map move handler from interfering
      commit("BUILDING_CHANGE_IN_PROGRESS", true);

      commit("CURRENT_BUILDING", building);

      // Update route parameters
      updateMapContentRoute(state);

      // Clear flag after a short delay to allow level selection to complete
      setTimeout(() => {
        commit("BUILDING_CHANGE_IN_PROGRESS", false);
      }, 1500);
    },
    async SET_CURRENT_LEVEL({ commit, state, dispatch }, payload) {
      if (!state.map || !state.isMapReady) {
        return;
      }
      const level = payload.level;
      commit("CURRENT_LEVEL", level);

      // Update route parameters
      updateMapContentRoute(state);

      if (level !== undefined) {
        dispatch("UNHIGHLIGHT_ALL_FEATURES");
        dispatch("ADD_MAP_OBJECTS_TO_MAP");
        commit("CONTENT/IS_ADDED_ON_MAP", { contentName: "mapObjects", isAddedOnMap: true }, { root: true });
      } else {
        dispatch("REMOVE_MAP_OBJECTS_FROM_MAP");
      }
    },
    REMOVE_MAP_OBJECTS_FROM_MAP({ state, commit }) {
      if (!state.map || !state.isMapReady) {
        return;
      }
      state?.map?.stop();
      if (state?.map?.getSource(MapConstants.SOURCE)) {
        state.map?.getSource(MapConstants.SOURCE)?.setData(MapConstants.EMPTY_GEO_JSON);
        commit("CONTENT/IS_ADDED_ON_MAP", { contentName: "mapObjects", isAddedOnMap: false }, { root: true });
      }
    },
    ADD_MAP_OBJECTS_TO_MAP({ state, rootState, rootGetters, dispatch, commit }, features) {
      if (!state.map || !state.isMapReady || !state.map?.getSource(MapConstants.SOURCE)) {
        return;
      }
      if (features) {
        state.map?.getSource(MapConstants.SOURCE)?.setData({
          type: "FeatureCollection",
          features: features.filter((feature) => feature.properties.lvl === state.currentLevel)
        });
      } else {
        let featureArray = [
          ...rootGetters["CONTENT/mapObjectsForSelectedPersona"]().filter(
            (feature) => feature.properties.lvl === state.currentLevel
          )
        ];
        rootState.CONTENT.contentVisibility.mapObjects = true;
        if (rootState.CONTENT.contentVisibility["beaconGeofences"]) {
          featureArray = [
            ...rootGetters["CONTENT/mapObjectsForSelectedPersona"]().filter(
              (feature) => feature.properties.lvl === state.currentLevel
            ),
            ...rootState.CONTENT.beaconGeofences
          ];
          commit("CONTENT/IS_ADDED_ON_MAP", { contentName: "beaconGeofences", isAddedOnMap: true }, { root: true });
        }
        if (state.shouldDisplayOriginalFloorPlan) {
          featureArray = [...rootState.CONTENT.originalMapObjects, ...featureArray];
        }
        state.map?.getSource(MapConstants.SOURCE)?.setData({
          type: "FeatureCollection",
          features: featureArray
        });
      }
      if (rootState.CONTENT.contentVisibility["beaconGeofences"]) {
        dispatch("ADD_GEOFENCES_TO_MAP");
      }
    },
    ADD_MAP_OBJECTS_FOR_BUILDING_ADD_EDIT({ state }, features) {
      if (!state.map || !state.isMapReady || !state.map?.getSource(MapConstants.SOURCE)) {
        return;
      }
      state.map?.getSource(MapConstants.SOURCE)?.setData({
        type: "FeatureCollection",
        features: features.filter(
          (feature) =>
            feature.properties.typeCode !== "beacon-geofence" && feature.properties.typeCode !== "gps-geofence"
        )
      });
    },
    SHOW_ORIGINAL_FLOOR_PLAN({ state, rootState }) {
      if (!state.map || !state.isMapReady || !state.map?.getSource(MapConstants.SOURCE)) {
        return;
      }
      let featureArray = [...rootState.CONTENT.mapObjects, ...rootState.CONTENT.originalMapObjects];
      if (rootState.CONTENT.contentVisibility["beaconGeofences"]) {
        featureArray = [
          ...rootState.CONTENT.mapObjects,
          ...rootState.CONTENT.originalMapObjects,
          ...rootState.CONTENT.beaconGeofences
        ];
      }
      state.map?.getSource(MapConstants.SOURCE)?.setData({
        type: "FeatureCollection",
        features: featureArray
      });
    },
    ADD_GEOFENCES_TO_MAP({ state, rootState, rootGetters, commit }, payload) {
      if (!state.map || !state.isMapReady || !state.map?.getSource(MapConstants.SOURCE)) {
        return;
      }
      let features = [
        ...rootGetters["CONTENT/mapObjectsForSelectedPersona"]().filter(
          (obj) => obj.properties.typeCode !== "beacon-geofence"
        ),
        ...rootGetters["CONTENT/beaconGeofencesForSelectedPersona"](),
        ...rootGetters["CONTENT/gpsGeofencesForSelectedPersona"]()
      ]
        .filter((feature) => feature?.properties?.lvl === state?.currentLevel || feature?.properties?.lvl === undefined)
        .filter((feature) => feature?.properties?.fid !== payload?.fid);

      if (state.shouldDisplayOriginalFloorPlan) {
        features = [...rootState.CONTENT.originalMapObjects, ...features];
      }

      features = features.map((feature) => {
        // Add personaColor property for geofences
        if (feature.properties.typeCode === "beacon-geofence" || feature.properties.typeCode === "gps-geofence") {
          const personas = PersonaHelpers.getPersonasForFeature(feature);
          // Use the first persona's color if exactly one persona matches, otherwise use default color
          const personaColor =
            rootState.CONTENT.mapPersonas?.length === 1 || personas.length > 1 ? "#346df1" : personas[0].color;
          // Create a copy of the feature to avoid mutating the original
          return {
            ...feature,
            properties: {
              ...feature.properties,
              personaColor
            }
          };
        }
        return feature;
      });

      state.map?.getSource(MapConstants.SOURCE)?.setData({
        type: "FeatureCollection",
        features
      });

      // Update fill layer paint properties to use personaColor
      const gpsGeofenceLayerId = "fill_gps-geofence_ptr";
      const beaconGeofenceLayerId = "fill_beacon-geofence_ptr";

      if (state.map?.getLayer(gpsGeofenceLayerId)) {
        state.map.setPaintProperty(gpsGeofenceLayerId, "fill-color", [
          "case",
          ["has", "personaColor"],
          ["get", "personaColor"],
          "#346DF1"
        ]);
      }

      if (state.map?.getLayer(beaconGeofenceLayerId)) {
        state.map.setPaintProperty(beaconGeofenceLayerId, "fill-color", [
          "case",
          ["has", "personaColor"],
          ["get", "personaColor"],
          "#346DF1" // Default color if personaColor is not set
        ]);
      }

      commit(
        "CONTENT/IS_ADDED_ON_MAP",
        [
          { contentName: "beaconGeofences", isAddedOnMap: true },
          { contentName: "gpsGeofences", isAddedOnMap: true },
          { contentName: "mapObjects", isAddedOnMap: true }
        ],
        { root: true }
      );
    },
    // this is for gps geofences only when we are in the gps geofence add edit view and we don't want to show level content
    ADD_GPS_GEOFENCES_TO_MAP({ state, rootState, rootGetters, commit }, payload) {
      if (!state.map || !state.isMapReady || !state.map?.getSource(MapConstants.SOURCE)) {
        return;
      }
      const features = rootGetters["CONTENT/gpsGeofencesForSelectedPersona"]()
        ?.filter((feature) => feature?.properties?.fid !== payload?.fid)
        .map((feature) => {
          // Add personaColor property for GPS geofences
          const personas = PersonaHelpers.getPersonasForFeature(feature);
          // Use the first persona's color if exactly one persona matches, otherwise use default color
          const personaColor =
            rootState.CONTENT.mapPersonas?.length === 1 || personas.length > 1 ? "#346df1" : personas[0].color;
          // Create a copy of the feature to avoid mutating the original
          return {
            ...feature,
            properties: {
              ...feature.properties,
              personaColor
            }
          };
        });

      state.map?.getSource(MapConstants.SOURCE)?.setData({
        type: "FeatureCollection",
        features
      });

      // Update fill layer paint property to use personaColor
      const gpsGeofenceLayerId = "fill_gps-geofence_ptr";
      if (state.map?.getLayer(gpsGeofenceLayerId)) {
        state.map.setPaintProperty(gpsGeofenceLayerId, "fill-color", [
          "case",
          ["has", "personaColor"],
          ["get", "personaColor"],
          "#346DF1" // Default color if personaColor is not set
        ]);
      }

      commit("CONTENT/IS_ADDED_ON_MAP", { contentName: "gpsGeofences", isAddedOnMap: true }, { root: true });
    },
    REMOVE_GEOFENCES_FROM_MAP({ state, rootState, commit }) {
      if (!state.map || !state.isMapReady || !state.map?.getSource(MapConstants.SOURCE)) {
        return;
      }
      state.map?.getSource(MapConstants.SOURCE)?.setData({
        type: "FeatureCollection",
        features: rootState.CONTENT.mapObjects.filter(
          (obj) => obj.properties.typeCode !== "beacon-geofence" && obj.properties.lvl === state.currentLevel
        )
      });
      commit(
        "CONTENT/IS_ADDED_ON_MAP",
        [
          { contentName: "beaconGeofences", isAddedOnMap: false },
          { contentName: "gpsGeofences", isAddedOnMap: false }
        ],
        { root: true }
      );
    },
    HIGHLIGHT_FEATURES({ state }, features) {
      if (!state.map || !state.isMapReady) {
        return;
      }
      state.map?.getSource(MapConstants.SELECTED_SOURCE)?.setData({
        type: "FeatureCollection",
        features: features
      });
    },
    UNHIGHLIGHT_ALL_FEATURES({ state }) {
      if (!state.map || !state.isMapReady) {
        return;
      }
      state.map?.getSource?.(MapConstants.SELECTED_SOURCE)?.setData(MapConstants.EMPTY_GEO_JSON);
    },
    DISPLAY_SITE_SYMBOLS({ state, rootState }) {
      if (!state.map || !state.isMapReady || !state.map?.getSource(MapConstants.SITE_OUTLINE_SOURCE)) {
        return;
      }
      const sitesGeoJson = {
        type: "FeatureCollection",
        features: rootState.CONTENT.sites
          .filter((site) => site.properties.fid !== state.currentSite) // Exclude current site
          .map((site) => {
            const turfSite = polygon(site.geometry.coordinates);
            return {
              type: "Feature",
              properties: {
                name: site.properties.name,
                typeCode: "site-outline",
                siteInternalIdentifier: site.properties.fid,
                siteExternalIdentifier: site.properties.eid
              },
              geometry: {
                type: "Point",
                coordinates: centroid(turfSite)?.geometry?.coordinates || []
              }
            };
          })
      };
      state.map?.getSource(MapConstants.SITE_OUTLINE_SOURCE)?.setData(sitesGeoJson);
    },
    HIDE_SITE_SYMBOLS({ state }) {
      if (!state.map || !state.isMapReady || !state.map?.getSource(MapConstants.SITE_OUTLINE_SOURCE)) {
        return;
      }
      state.map?.getSource(MapConstants.SITE_OUTLINE_SOURCE)?.setData(MapConstants.EMPTY_GEO_JSON);
    },
    DISPLAY_SITE_BORDERS({ state, rootState }) {
      if (!state.map || !state.isMapReady || !state.map?.getSource(MapConstants.SITE_BORDERS_SOURCE)) {
        return;
      }
      const sitesGeoJson = {
        type: "FeatureCollection",
        features: rootState.CONTENT.sites.map((site, index) => {
          return {
            type: "Feature",
            id: index,
            properties: {
              name: site?.properties?.name,
              typeCode: "site-outline",
              ...site.properties
            },
            geometry: {
              type: "Polygon",
              coordinates: site?.geometry?.coordinates || []
            }
          };
        })
      };
      state.map?.getSource(MapConstants.SITE_BORDERS_SOURCE)?.setData(sitesGeoJson);
    },
    HIDE_SITE_BORDERS({ state }) {
      if (!state.map || !state.isMapReady || !state.map?.getSource(MapConstants.SITE_BORDERS_SOURCE)) {
        return;
      }
      state.map?.getSource(MapConstants.SITE_BORDERS_SOURCE)?.setData({
        type: "FeatureCollection",
        features: []
      });
    },
    DISPLAY_BUILDING_BORDERS({ state, rootState, dispatch }, payload) {
      if (!state.map || !state.isMapReady || !state.map?.getSource(MapConstants.BUILDING_BORDERS_SOURCE)) {
        return;
      }
      const buildingsGeoJson = {
        type: "FeatureCollection",
        features: rootState.CONTENT.currentSiteData.buildings
          .filter((building) => {
            if (payload?.enableCurrentBuildingFilter) {
              return building.properties.fid !== state.currentBuilding;
            } else {
              return true;
            }
          })
          .map((building) => {
            return {
              type: "Feature",
              id: building.properties.mapId, // this is for map highlighting. It requires number id to set feature state of the map
              properties: {
                name: building.properties.name,
                typeCode: "building-outline",
                buildingInternalIdentifier: building.properties.fid,
                buildingExternalIdentifier: building.properties.eid,
                ...building.properties
              },
              geometry: {
                type: "Polygon",
                coordinates: building?.geometry?.coordinates || []
              }
            };
          })
      };
      state.map?.getSource(MapConstants.BUILDING_BORDERS_SOURCE)?.setData(buildingsGeoJson);

      if (state.map.getLayer("fill_building-outline_ptr")) {
        state.map.setPaintProperty("fill_building-outline_ptr", "fill-extrusion-opacity", payload?.opacity || 1);
      }

      dispatch("DISPLAY_BUILDING_SYMBOLS", payload);
    },
    HIDE_BUILDING_BORDERS({ state, dispatch }) {
      if (!state.map || !state.isMapReady || !state.map?.getSource(MapConstants.BUILDING_BORDERS_SOURCE)) {
        return;
      }
      state.map?.getSource(MapConstants.BUILDING_BORDERS_SOURCE)?.setData(MapConstants.EMPTY_GEO_JSON);
      dispatch("HIDE_BUILDING_SYMBOLS");
    },
    DISPLAY_BUILDING_SYMBOLS({ state, rootState }, payload) {
      if (!state.map || !state.isMapReady || !state.map?.getSource(MapConstants.BUILDING_OUTLINE_SOURCE)) {
        return;
      }
      const buildingsGeoJson = {
        type: "FeatureCollection",
        features: []
      };
      rootState.CONTENT.currentSiteData.buildings
        .filter((building) => {
          if (payload?.enableCurrentBuildingFilter) {
            return building.properties.fid !== state.currentBuilding && building.geometry?.coordinates;
          } else {
            return building.geometry?.coordinates;
          }
        })
        .forEach((building) => {
          const turfBuilding = polygon(building.geometry?.coordinates);
          buildingsGeoJson.features.push({
            type: "Feature",
            id: building.properties.fid,
            properties: {
              name: building.properties.name,
              typeCode: "building-outline",
              buildingInternalIdentifier: building.properties.fid,
              buildingExternalIdentifier: building.properties.eid
            },
            geometry: {
              type: "Point",
              coordinates: centroid(turfBuilding)?.geometry?.coordinates || []
            }
          });
        });
      state.map?.getSource(MapConstants.BUILDING_OUTLINE_SOURCE)?.setData(buildingsGeoJson);
    },
    HIDE_BUILDING_SYMBOLS({ state }) {
      if (!state.map || !state.isMapReady || !state.map?.getSource(MapConstants.BUILDING_OUTLINE_SOURCE)) {
        return;
      }
      state.map?.getSource(MapConstants.BUILDING_OUTLINE_SOURCE)?.setData(MapConstants.EMPTY_GEO_JSON);
    },
    DRAWN_POLYGON_CHANGED({ state, commit }, { feature, shouldKeepFormClean }) {
      if (!shouldKeepFormClean) {
        commit("CONTENT/IS_FORM_DIRTY", true, { root: true });
      }
      if (feature === undefined) {
        state.drawnCoordinates = undefined;
        state.mapDraw?.deleteAll();
        return;
      }
      const drawnPolygon = state.mapDraw?.getAll()?.features?.[0];
      if (JSON.stringify(drawnPolygon?.geometry?.coordinates) === state.drawnCoordinates) {
        return;
      }
      state.drawnCoordinates = JSON.stringify(feature.geometry.coordinates);

      // Create a copy for drawing instead of mutating the original feature
      const drawFeature = { ...feature };
      if (drawFeature?.properties?.fid) {
        drawFeature.id = drawFeature.properties.fid;
      }

      state.mapDraw.add(drawFeature);
    },
    // TODO: If another mode comes in refactor these to one function
    MAP_ENTER_EDIT_MODE({ state, rootState }) {
      if (!state.map || !state.isMapReady) {
        return;
      }
      rootState.CONTENT.mapObjects.forEach((mapObject) => {
        state.map.setFeatureState({ source: "source_ptr", id: mapObject.id }, { edit: true });
      });
    },
    MAP_EXIT_EDIT_MODE({ state, rootState }) {
      if (!state.map || !state.isMapReady) {
        return;
      }
      rootState.CONTENT.mapObjects.forEach((mapObject) => {
        state.map.setFeatureState({ source: "source_ptr", id: mapObject.id }, { edit: false });
      });
    },
    MAP_ENTER_WAYFINDING_MODE({ state, rootState }) {
      if (!state.map || !state.isMapReady) {
        return;
      }
      rootState.CONTENT.mapObjects.forEach((mapObject) => {
        state.map.setFeatureState({ source: "source_ptr", id: mapObject.id }, { "wayfinding-edit": true });
      });
    },
    MAP_EXIT_WAYFINDING_MODE({ state, rootState }) {
      if (!state.map || !state.isMapReady) {
        return;
      }
      rootState.CONTENT.mapObjects.forEach((mapObject) => {
        state.map.setFeatureState({ source: "source_ptr", id: mapObject.id }, { "wayfinding-edit": false });
      });
    },
    SET_DRAW_POLYGON_LAYOUT_VISIBILITY({ commit }, { visible }) {
      const visibility = visible ? "visible" : "none";
      commit("SET_MAP_LAYOUT_PROPERTY", {
        layer: "gl-draw-polygon-fill-inactive.cold",
        property: "visibility",
        value: visibility
      });
      commit("SET_MAP_LAYOUT_PROPERTY", {
        layer: "gl-draw-polygon-stroke-inactive.cold",
        property: "visibility",
        value: visibility
      });
    },
    CLEAR_EXPANDED_TYPECODE_BEFORE_EDIT({ commit }) {
      commit("SET_EXPANDED_TYPECODE_BEFORE_EDIT", { buildingId: null, levelId: null, typeCode: null });
    },
    async GET_COPYRIGHT_TEXT({ commit }, mapProviderName) {
      if (mapProviderName !== "esri") {
        commit("SET_IS_ESRI_MAP", false);
        commit("SET_ESRI_COPYRIGHT_TEXT", undefined);
        return;
      }
      commit("SET_IS_ESRI_MAP", true);
      const esriCopyrightText = await StyleJsonService.getEsriAttributionText();
      commit("SET_ESRI_COPYRIGHT_TEXT", esriCopyrightText);
      return;
    }
  },
  getters: {
    map: (state) => state.map,
    currentClientObject: (state, _getters, rootState) => {
      return rootState.CONTENT.clients.find((client) => client.identifier === state.currentClient);
    },
    currentSiteObject: (state, _getters, rootState) => {
      return (
        rootState.CONTENT?.sites.find((site) => site.properties.fid === state.currentSite) || rootState.CONTENT?.site
      );
    },
    currentBuildingObject: (state, _getters, rootState) => {
      if (state.currentBuilding === undefined) {
        return;
      }
      //TODO: remove/update buildings & levels at the end of fluid implementation
      return (
        rootState.CONTENT.buildings.find((building) => building.properties.fid === state.currentBuilding) ||
        rootState.CONTENT.currentSiteData.buildings.find(
          (building) => building.properties.fid === state.currentBuilding
        )
      );
    },
    currentLevelObject: (state, _getters, rootState) => {
      const levels =
        rootState.CONTENT.levels.length > 0 ? rootState.CONTENT.levels : rootState.CONTENT.currentSiteData.levels;
      return levels.find((level) => level.properties.lvl === Number(state.currentLevel));
    },
    currentLevelBeacons: (state, _getters, rootState) => {
      return rootState.CONTENT.beacons.filter(
        (beacon) =>
          beacon.properties.lvl === Number(state.currentLevel) && beacon.properties.bid === state.currentBuilding
      );
    }
  }
};
